import { useState } from "react";
import { AdminOrgs, Orgs } from "../../lib/apiTyped";
import { useResource } from "../../lib/useResource";
import { useApiCall } from "../../lib/useToastError";
import { useNotifications } from "../shared/NotificationsProvider";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { Spinner } from "../shared/Spinner";
import { StatusBadge } from "../shared/StatusBadge";
import { Badge } from "../shared/Card";
import { fmtDate } from "../../lib/format";

export function OrgDetailModal({
  orgId,
  onClose,
  onChanged,
}: {
  orgId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { data, loading, refetch } = useResource(
    () => AdminOrgs.details(orgId),
    [orgId],
  );
  const { run } = useApiCall();
  const { confirm } = useNotifications();

  const [tier, setTier] = useState<"none" | "tier-1" | "tier-2">("none");
  const [seats, setSeats] = useState(0);

  // Initialise plan inputs when data lands.
  if (data && tier === "none" && data.org.activeTier) {
    setTier(data.org.activeTier);
    setSeats(data.org.seatCount ?? 0);
  }

  if (loading || !data) {
    return (
      <Modal onClose={onClose} maxWidth="42rem">
        <div className="py-16 flex items-center justify-center">
          <Spinner />
        </div>
      </Modal>
    );
  }

  const { org, members, seatsInUse } = data;

  async function savePlan() {
    const target = tier === "none" ? null : tier;
    await run(() => AdminOrgs.setPlan(orgId, target, seats), {
      successMessage: "Plan updated.",
    });
    await refetch();
    onChanged();
  }
  async function ensureStripe() {
    await run(() => Orgs.ensureStripeCustomer(orgId), {
      successMessage: "Stripe customer ready.",
    });
    await refetch();
  }
  async function suspendOrUnsuspend(action: "suspend" | "reactivate") {
    const ok = await confirm({
      title: action === "suspend" ? "Suspend org?" : "Reactivate org?",
      message:
        action === "suspend"
          ? "API access stops immediately. The owner sees the suspension."
          : "API access resumes immediately.",
      confirmLabel: action === "suspend" ? "Suspend" : "Reactivate",
      variant: action === "suspend" ? "danger" : "neutral",
    });
    if (!ok) return;
    await run(() => AdminOrgs.suspend(orgId, action), {
      successMessage: action === "suspend" ? "Suspended." : "Reactivated.",
    });
    await refetch();
    onChanged();
  }
  async function deleteOrg() {
    const ok = await confirm({
      title: "Delete this org permanently?",
      message:
        "All members, API keys, invites, proposals and usage are wiped. The org must already be suspended.",
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    const r = await run(() => AdminOrgs.delete(orgId), {
      successMessage: "Org deleted.",
    });
    if (r) {
      onChanged();
      onClose();
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="48rem">
      <div className="flex items-center gap-3 mb-1 pr-8">
        <h3 className="text-xl font-black text-ink">{org.name}</h3>
        <StatusBadge status={org.status} />
        <Badge label={org.type} color="#71717a" />
      </div>
      <p className="text-xs text-ink-3 break-all">
        <code>{org.id}</code>
      </p>

      <div className="mt-6 space-y-6">
        <Section title="Plan & seats">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1 text-ink-3">
                Tier
              </label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value as typeof tier)}
                className="rounded-xl px-3 py-2 text-sm"
                style={{
                  backgroundColor: "rgba(255,255,255,0.6)",
                  border: "1px solid rgba(0,0,0,0.14)",
                }}
              >
                <option value="none">No plan</option>
                <option value="tier-1">tier-1</option>
                <option value="tier-2">tier-2</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-ink-3">
                Seats
              </label>
              <input
                type="number"
                min={0}
                value={seats}
                onChange={(e) => setSeats(Math.max(0, Number(e.target.value) || 0))}
                className="rounded-xl px-3 py-2 text-sm w-24"
                style={{
                  backgroundColor: "rgba(255,255,255,0.6)",
                  border: "1px solid rgba(0,0,0,0.14)",
                }}
              />
            </div>
            <Button variant="primary" size="sm" onClick={savePlan}>
              Save plan
            </Button>
            <span className="text-xs text-ink-3">In use: {seatsInUse}</span>
          </div>
        </Section>

        <Section title="Stripe customer">
          <div className="flex flex-wrap items-center gap-3">
            {org.stripeCustomerId ? (
              <code className="text-xs text-ink-2 break-all">
                {org.stripeCustomerId}
              </code>
            ) : (
              <span className="text-sm text-ink-3">Not yet provisioned.</span>
            )}
            {!org.stripeCustomerId && org.fiscal && (
              <Button variant="primary" size="sm" onClick={ensureStripe}>
                Create
              </Button>
            )}
          </div>
        </Section>

        <Section title="Members">
          <ul className="divide-y divide-border">
            {members.map((m) => (
              <li
                key={m.uid}
                className="py-2 flex items-center justify-between text-sm"
              >
                <span className="text-ink">{m.email}</span>
                <span className="flex items-center gap-2">
                  <Badge label={m.role} color="#71717a" />
                </span>
              </li>
            ))}
            {members.length === 0 && (
              <li className="py-2 text-sm text-ink-3">No members yet.</li>
            )}
          </ul>
        </Section>

        <Section title="Danger zone">
          <div className="flex flex-wrap gap-3">
            {org.status === "suspended" ? (
              <Button variant="primary" size="sm" onClick={() => suspendOrUnsuspend("reactivate")}>
                Reactivate
              </Button>
            ) : (
              <Button variant="danger" size="sm" onClick={() => suspendOrUnsuspend("suspend")}>
                Suspend org
              </Button>
            )}
            <Button
              variant="danger"
              size="sm"
              onClick={deleteOrg}
              disabled={org.status !== "suspended"}
            >
              Delete org permanently
            </Button>
          </div>
        </Section>

        <p className="text-xs text-ink-3 pt-4 border-t border-border">
          Created {fmtDate(org.createdAt)}
        </p>
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
